import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Modal from '../../ui/Modal';
import {
  selectSelectTierModalOpen,
  selectCommissionTiers,
  closeSelectTierModal,
  updateRewardForm,
} from '../../../store/slices/gamificationSlice';

const SelectTierModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectSelectTierModalOpen);
  const tiers = useSelector(selectCommissionTiers);
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSave = () => {
    if (selectedTierId) {
      const selectedTier = tiers.find(t => t.id === selectedTierId);
      dispatch(updateRewardForm({ field: 'rewardConfig.tierId', value: selectedTierId }));
      dispatch(updateRewardForm({ field: 'rewardConfig.tierName', value: selectedTier.name }));
      dispatch(closeSelectTierModal());
      setSelectedTierId(null);
    }
  };

  const handleClose = () => {
    setSelectedTierId(null);
    setIsDropdownOpen(false);
    dispatch(closeSelectTierModal());
  };

  const selectedTier = tiers.find(t => t.id === selectedTierId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Select a commission tier"
      size="sm"
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            className="px-6 py-2.5 bg-white border-2 border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Go Back
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!selectedTierId}
            className={`
              px-6 py-2.5 rounded-xl text-sm font-medium transition-colors
              ${!selectedTierId
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary/90'
              }
            `}
          >
            Save
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-600">
          Upgrade to <span className="text-red-500">*</span>
        </label>

        <div className="relative w-full" ref={dropdownRef}>
          {/* Trigger Button */}
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`
              w-full px-4 py-2.5 rounded-lg border-2 transition-all duration-200 text-sm
              flex items-center justify-between
              ${isDropdownOpen ? 'border-purple-500' : 'border-gray-300'}
              bg-white hover:border-purple-400
              focus:outline-none
            `}
          >
            <span className={selectedTier ? 'text-gray-900' : 'text-gray-400'}>
              {selectedTier ? selectedTier.name : 'Select a tier'}
            </span>
            {isDropdownOpen ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute z-[9999] w-full mt-2 bg-white border-2 border-purple-500 rounded-lg shadow-lg overflow-hidden">
              {tiers.map((tier) => (
                <button
                  key={tier.id}
                  type="button"
                  onClick={() => {
                    setSelectedTierId(tier.id);
                    setIsDropdownOpen(false);
                  }}
                  className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-purple-50 transition-colors"
                >
                  {tier.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SelectTierModal;
